import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMoneyCheckDollar } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import { useGetLoansByBorrowerQuery } from "../../services/loanApiSlice";
import {
  useMakePaymentMutation,
  useGetPaymentsByBorrowerQuery,
} from "../../services/loanPaymentApiSlice";
import { useGetRepaymentScheduleByLoanIdQuery } from "../../services/repaymentScheduleApiSlice";
import { useGetScheduleSummaryByLoanIdQuery } from "../../services/loanPaymentAllocationApiSlice";
import {
  Check,
  Clock,
  AlertCircle,
  Calendar,
  Hash,
  DollarSign,
  FileText,
  CreditCard,
  Landmark,
  Smartphone,
  AlertTriangle,
  BadgeCheck,
} from "lucide-react";

const Repayment = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const borrowerId = user?.borrowerId || user?.id || user?.userId || null;
  const [selectedLoan, setSelectedLoan] = useState("");

  // Loans
  const { data: loansData = [], isLoading: loansLoading } = useGetLoansByBorrowerQuery(borrowerId, {
    skip: !borrowerId,
  });
  const loans = useMemo(() => 
    loansData.filter((loan) => loan.status?.toLowerCase() === "active"), 
    [loansData]
  );

  // Payments
  const { data: payments = [], isLoading: paymentsLoading, refetch: refetchPayments } = useGetPaymentsByBorrowerQuery(
    borrowerId,
    { skip: !borrowerId }
  );
  const [makePayment, { isLoading: isMakingPayment }] = useMakePaymentMutation();

  // Repayment form state
  const [repayment, setRepayment] = useState({
    loanId: "",
    scheduleId: "",
    amount: "",
    date: "",
    paymentMethod: "Cash",
    referenceNo: "",
    remarks: "",
  });

  // State for schedule summary data (payment allocations)
  const [scheduleSummary, setScheduleSummary] = useState([]);

  // Fetch schedule summary (payment allocations) for selected loan
  const { data: scheduleSummaryData = [], isLoading: scheduleSummaryLoading, refetch: refetchScheduleSummary } = useGetScheduleSummaryByLoanIdQuery(
    selectedLoan,
    { skip: !selectedLoan }
  );

  // Store schedule summary in state and localStorage
  useEffect(() => {
    if (scheduleSummaryData && scheduleSummaryData.length > 0) {
      const mappedData = scheduleSummaryData.map((item) => ({
        scheduleId: item.ScheduleId || item.id,
        loanId: item.LoanId,
        installmentNo: item.InstallmentNo,
        dueDate: item.DueDate,
        paidSoFar: item.PaidSoFar ?? 0,
        overduePaidSoFar: item.OverduePaidSoFar ?? 0,
        paidTotalSoFar: item.PaidTotalSoFar ?? 0,
      }));
      setScheduleSummary(mappedData);
      localStorage.setItem("scheduleSummary", JSON.stringify(mappedData));
    }
  }, [scheduleSummaryData]);

  // Preselect loan from localStorage or fallback to first
  useEffect(() => {
    if (loans.length > 0) {
      const storedLoanId = localStorage.getItem("selectedLoanId");
      let targetLoan = null;
      if (storedLoanId) {
        targetLoan = loans.find(
          (loan) => (loan.loanId || loan.id) === parseInt(storedLoanId)
        );
        localStorage.removeItem("selectedLoanId");
      }
      const loanId = targetLoan
        ? targetLoan.loanId || targetLoan.id
        : loans[0].loanId || loans[0].id;
      setRepayment((prev) => ({ ...prev, loanId }));
      setSelectedLoan(loanId);
    }
  }, [loans]);

  // Fetch schedule for selected loan
  const { data: schedules = [], isLoading: schedulesLoading, refetch: refetchSchedules } = useGetRepaymentScheduleByLoanIdQuery(
    selectedLoan,
    { skip: !selectedLoan }
  );

  // Normalize schedule fields with payment allocation data
  const normalizedSchedules = useMemo(() => {
    return schedules.map((s) => {
      const scheduleId = s.scheduleId || s.id || s.repaymentScheduleId || s.paymentScheduleId || s.schedule_id;
      const totalDue = s.totalDue ?? s.dueAmount ?? s.amount ?? 0;
      const feesDue = s.feesDue ?? s.lateFee ?? s.penalty ?? 0;
      
      // Find corresponding payment summary for this schedule
      const summary = scheduleSummary.find((item) => item.scheduleId === scheduleId) || {};
      const paidTotalSoFar = summary.paidTotalSoFar ?? 0;
      const overduePaidSoFar = summary.overduePaidSoFar ?? 0;
      
      // Calculate amounts left to pay
      const totalAmountDue = totalDue + feesDue;
      const amountLeftToPay = Math.max(0, totalAmountDue - paidTotalSoFar);
      const overdueLeftToPay = Math.max(0, (feesDue - overduePaidSoFar));
      
      return {
        scheduleId,
        loanId: s.loanId || selectedLoan,
        loanProductName: s.loanProductName || "Loan",
        installmentNo: s.installmentNo || s.installment_number || s.installment || "—",
        dueDate: s.dueDate || s.paymentDate || s.scheduleDate || s.dueAt,
        totalDue,
        feesDue,
        status: s.status || "Pending",
        paidSoFar: summary.paidSoFar ?? 0,
        paidTotalSoFar,
        amountLeftToPay,
        overdueLeftToPay,
        totalAmountDue,
      };
    });
  }, [schedules, selectedLoan, scheduleSummary]);

  // Preselect first unpaid schedule and prefill amount (use amount left to pay for partially paid)
  useEffect(() => {
    if (normalizedSchedules.length > 0) {
      const unpaid = normalizedSchedules.find((s) => s.amountLeftToPay > 0) || normalizedSchedules[0];
      const scheduleId = unpaid.scheduleId;
      const amountToPay = unpaid.amountLeftToPay;

      setRepayment((prev) => ({
        ...prev,
        scheduleId: scheduleId || prev.scheduleId,
        amount: amountToPay > 0 ? String(amountToPay) : prev.amount,
      }));
    }
  }, [normalizedSchedules]);

  // ========== AUTO‑FILL HELPERS ==========
  const generateReferenceNo = (loanId, installmentNo) => {
    return `REF-${loanId}-${installmentNo}-${Date.now()}`;
  };

  const generateRemarks = (schedule) => {
    if (!schedule) return '';
    const { installmentNo, totalDue, paidTotalSoFar, amountLeftToPay } = schedule;
    const formattedTotal = formatCurrency(totalDue);
    const formattedPaid = formatCurrency(paidTotalSoFar);
    const formattedLeft = formatCurrency(amountLeftToPay);

    if (paidTotalSoFar === 0) {
      return `Full payment for Installment #${installmentNo} (Total due: ${formattedTotal})`;
    } else {
      return `Remaining payment for Installment #${installmentNo} (Previously paid: ${formattedPaid}, Remaining: ${formattedLeft})`;
    }
  };

  // Auto‑fill form when scheduleId changes
  useEffect(() => {
    if (repayment.scheduleId && normalizedSchedules.length > 0) {
      const selectedSchedule = normalizedSchedules.find(s => 
        s.scheduleId.toString() === repayment.scheduleId.toString()
      );
      if (selectedSchedule) {
        const today = new Date().toISOString().split('T')[0];
        const amountLeft = selectedSchedule.amountLeftToPay;
        const refNo = generateReferenceNo(selectedSchedule.loanId, selectedSchedule.installmentNo);
        const remarks = generateRemarks(selectedSchedule);

        setRepayment(prev => ({
          ...prev,
          amount: amountLeft > 0 ? String(amountLeft) : '',
          date: today,
          referenceNo: refNo,
          remarks: remarks,
        }));
      }
    }
  }, [repayment.scheduleId, normalizedSchedules]);

  // ========== HANDLERS ==========
  const handleLoanChange = (loanId) => {
    setSelectedLoan(loanId);
    setRepayment((prev) => ({ ...prev, loanId, scheduleId: "", amount: "" }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRepayment((prev) => ({ ...prev, [name]: value }));
  };

  // Ref for the payment form container (kept for potential future use)
  const formRef = useRef();

  const handleMakePaymentClick = (schedule) => {
    setRepayment(prev => ({
      ...prev,
      scheduleId: String(schedule.scheduleId),
      // amount, date, reference, remarks will be auto‑filled by the effect above
    }));
    // Scroll smoothly to the very top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!borrowerId) return alert("Please log in to make a repayment.");
    if (!repayment.loanId || !repayment.scheduleId || !repayment.amount || !repayment.date) {
      return alert("Please select schedule and fill loan, amount and date.");
    }
    
    const paymentAmount = Number(repayment.amount);
    if (paymentAmount <= 0) {
      return alert("Payment amount must be greater than 0.");
    }

    const payload = {
      borrowerId,
      loanId: Number(repayment.loanId),
      scheduleId: Number(repayment.scheduleId),
      paidAmount: Number(repayment.amount),
      paymentMethod: repayment.paymentMethod,
      referenceNo: repayment.referenceNo || null,
      remarks: repayment.remarks || null,
    };

    try {
      const res = await makePayment(payload).unwrap();
      alert(res?.Result || res?.Message || "Payment scheduled/processed successfully.");
      
      // Refetch all data to update with new payment allocations
      refetchScheduleSummary();
      refetchSchedules();
      refetchPayments();
      
      setRepayment((prev) => ({
        ...prev,
        amount: "",
        date: "",
        referenceNo: "",
        remarks: "",
      }));
      
      navigate("/payment-allocation-history", { state: { refresh: Date.now() } });
    } catch (err) {
      console.error("MakePayment error:", err);
      const serverMsg = err?.data?.Result || err?.data?.Message || err?.error || err?.message;
      alert(serverMsg || "Failed to make payment. Please try again.");
    }
  };

  // Determine if the selected schedule is fully paid (to disable submit button)
  const selectedScheduleForPayment = useMemo(() => {
    if (!repayment.scheduleId) return null;
    return normalizedSchedules.find(s => 
      s.scheduleId.toString() === repayment.scheduleId.toString()
    );
  }, [repayment.scheduleId, normalizedSchedules]);

  // Check if there are any incomplete prior schedules (for sequential payment validation)
  const incompletePriorSchedule = useMemo(() => {
    if (!selectedScheduleForPayment || !normalizedSchedules.length) return null;
    
    const currentInstallment = selectedScheduleForPayment.installmentNo;
    
    // Find any schedule that comes before the current one (by installment number) that has amount left to pay
    const priorIncomplete = normalizedSchedules.find(s => {
      const priorInstallment = s.installmentNo;
      // Compare installment numbers (handle both strings and numbers)
      const priorNum = typeof priorInstallment === 'number' ? priorInstallment : parseInt(priorInstallment);
      const currentNum = typeof currentInstallment === 'number' ? currentInstallment : parseInt(currentInstallment);
      
      return priorNum < currentNum && s.amountLeftToPay > 0;
    });
    
    return priorIncomplete || null;
  }, [selectedScheduleForPayment, normalizedSchedules]);

  const paymentAmount = Number(repayment.amount);
  
  // Calculate max allowed amount for the selected schedule
  const maxAllowedAmount = useMemo(() => {
    if (!selectedScheduleForPayment) return 0;
    // If there's amount left to pay, that's the max
    if (selectedScheduleForPayment.amountLeftToPay > 0) {
      return selectedScheduleForPayment.amountLeftToPay;
    }
    // Otherwise (fully paid), use the total amount due
    return selectedScheduleForPayment.totalAmountDue;
  }, [selectedScheduleForPayment]);
  
  const isConfirmDisabled = isMakingPayment || 
    (selectedScheduleForPayment && selectedScheduleForPayment.amountLeftToPay <= 0) ||
    (incompletePriorSchedule !== null) ||
    paymentAmount <= 0 ||
    paymentAmount > maxAllowedAmount;

  // ========== STYLING HELPERS ==========
  const getStatusIcon = (status) => {
    const s = status?.toLowerCase() || "";
    if (s.includes("paid") || s.includes("completed"))
      return <BadgeCheck className="w-4 h-4 text-emerald-600" />;
    if (s.includes("overdue") || s.includes("late"))
      return <AlertCircle className="w-4 h-4 text-rose-600" />;
    if (s.includes("partial"))
      return <Clock className="w-4 h-4 text-amber-600" />;
    return <Clock className="w-4 h-4 text-amber-600" />;
  };

  const getStatusClasses = (status) => {
    const s = status?.toLowerCase() || "";
    if (s.includes("paid") || s.includes("completed"))
      return "bg-emerald-50 text-emerald-700 border-emerald-200 ring-1 ring-emerald-200/50";
    if (s.includes("overdue") || s.includes("late"))
      return "bg-rose-50 text-rose-700 border-rose-200 ring-1 ring-rose-200/50";
    if (s.includes("partial"))
      return "bg-amber-50 text-amber-700 border-amber-200 ring-1 ring-amber-200/50";
    if (s.includes("pending") || s.includes("processing"))
      return "bg-amber-50 text-amber-700 border-amber-200 ring-1 ring-amber-200/50";
    return "bg-slate-100 text-slate-700 border-slate-200 ring-1 ring-slate-200/50";
  };

  const getCardAccentColor = (status) => {
    const s = status?.toLowerCase() || "";
    if (s.includes("paid") || s.includes("completed")) return "from-emerald-500 to-emerald-400";
    if (s.includes("overdue") || s.includes("late")) return "from-rose-500 to-rose-400";
    if (s.includes("partial")) return "from-amber-500 to-amber-400";
    if (s.includes("pending") || s.includes("processing")) return "from-blue-500 to-blue-400";
    return "from-slate-400 to-slate-300";
  };

  // Enhanced card background with more distinct gradients and a subtle shadow by default
  const getCardBackground = (status) => {
    const s = status?.toLowerCase() || "";
    if (s.includes("paid") || s.includes("completed"))
      return "bg-gradient-to-br from-emerald-50 to-white";
    if (s.includes("overdue") || s.includes("late"))
      return "bg-gradient-to-br from-rose-50 to-white";
    if (s.includes("partial"))
      return "bg-gradient-to-br from-amber-50 to-white";
    if (s.includes("pending") || s.includes("processing"))
      return "bg-gradient-to-br from-blue-50 to-white";
    return "bg-white";
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "LKR" }).format(amount || 0);

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "—";

  const getDueStatus = (dueDate, status) => {
    if (!dueDate) return null;
    if (status.toLowerCase().includes("paid")) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { label: `Overdue by ${Math.abs(diffDays)} days`, type: "overdue" };
    if (diffDays === 0) return { label: "Due today", type: "today" };
    if (diffDays === 1) return { label: "Due tomorrow", type: "upcoming" };
    if (diffDays <= 7) return { label: `Due in ${diffDays} days`, type: "upcoming" };
    return { label: `Due on ${formatDate(dueDate)}`, type: "future" };
  };

  // Stats
  const stats = useMemo(() => {
    const pending = normalizedSchedules
      .filter((s) => s.status.toLowerCase() === "pending")
      .reduce((sum, s) => sum + s.amountLeftToPay, 0);
    const overdue = normalizedSchedules
      .filter((s) => s.status.toLowerCase() === "overdue")
      .reduce((sum, s) => sum + s.amountLeftToPay, 0);
    const paid = payments
      .filter((p) => p.paidAmount)
      .reduce((sum, p) => sum + p.paidAmount, 0);
    return { pending, overdue, paid };
  }, [normalizedSchedules, payments]);

  // Payment method icons
  const paymentMethodIcon = {
    Cash: <DollarSign className="w-4 h-4" />,
    "Bank Transfer": <Landmark className="w-4 h-4" />,
    "Mobile Money": <Smartphone className="w-4 h-4" />,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 p-4 sm:p-6 lg:p-8">
      {/* Subtle background pattern */}
      <div className="fixed inset-0 -z-10 h-full w-full bg-white bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-20"></div>

      <div className="max-w-7xl mx-auto space-y-8 relative">
        {/* ========== HEADER ========== */}
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-blue-600 to-blue-500 rounded-xl shadow-lg shadow-blue-200/50">
            <FontAwesomeIcon
              icon={faMoneyCheckDollar}
              className="w-6 h-6 text-white"
            />
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
              Payment Tracking
            </h1>
            <p className="text-slate-500 mt-1.5 text-base flex items-center gap-2">
              <span className="inline-block w-1 h-1 bg-blue-500 rounded-full"></span>
              Monitor and manage your loan repayments
            </p>
          </div>
        </div>

        {/* ========== LOAN SELECTOR ========== */}
        {loansLoading ? (
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-12 w-40 bg-slate-200 rounded-full animate-pulse"
              />
            ))}
          </div>
        ) : loans.length > 0 ? (
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {loans.map((loan) => {
              const loanId = loan.loanId || loan.id;
              const isSelected = selectedLoan === loanId;
              return (
                <button
                  key={loanId}
                  onClick={() => handleLoanChange(loanId)}
                  className={`
                    px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap
                    ${
                      isSelected
                        ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-200 scale-[1.02]"
                        : "bg-white/80 backdrop-blur-sm text-slate-700 border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 hover:shadow-md"
                    }
                  `}
                >
                  {loan.loanProductName || `Loan #${loanId}`}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-slate-200 p-8 text-center shadow-sm">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-slate-100 rounded-full mb-4">
              <FileText className="w-6 h-6 text-slate-500" />
            </div>
            <p className="text-slate-600 font-medium">No active loans available</p>
            <p className="text-slate-400 text-sm mt-1">
              Once you have an active loan, it will appear here.
            </p>
          </div>
        )}

        {/* ========== STATS CARDS ========== */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Pending */}
          <div className="group bg-white/90 backdrop-blur-sm rounded-2xl border border-amber-100 p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl shadow-md shadow-amber-200">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Pending & Overdue</p>
                <p className="text-2xl font-bold text-slate-900 mt-0.5">
                  {formatCurrency(stats.pending + stats.overdue)}
                </p>
              </div>
            </div>
          </div>
          {/* Overdue (separate card) */}
          <div className="group bg-white/90 backdrop-blur-sm rounded-2xl border border-rose-100 p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-rose-500 to-rose-600 rounded-xl shadow-md shadow-rose-200">
                <AlertCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Overdue Amount</p>
                <p className="text-2xl font-bold text-slate-900 mt-0.5">
                  {formatCurrency(stats.overdue)}
                </p>
              </div>
            </div>
          </div>
          {/* Total Paid */}
          <div className="group bg-white/90 backdrop-blur-sm rounded-2xl border border-emerald-100 p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-md shadow-emerald-200">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Total Paid</p>
                <p className="text-2xl font-bold text-slate-900 mt-0.5">
                  {formatCurrency(stats.paid)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ========== MAIN GRID ========== */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT COLUMN – SCHEDULE CARDS */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <div className="p-1.5 bg-blue-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                Payment Schedule
              </h2>
              <span className="text-sm px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full font-medium">
                {normalizedSchedules.length} installments
              </span>
            </div>

            {schedulesLoading ? (
              <div className="space-y-5">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-white/90 backdrop-blur-sm rounded-2xl border border-slate-200 p-6 space-y-5 animate-pulse"
                  >
                    <div className="flex justify-between">
                      <div className="h-5 bg-slate-200 rounded w-1/4"></div>
                      <div className="h-5 bg-slate-200 rounded w-1/6"></div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="h-4 bg-slate-200 rounded w-full"></div>
                      <div className="h-4 bg-slate-200 rounded w-full"></div>
                      <div className="h-4 bg-slate-200 rounded w-full"></div>
                      <div className="h-4 bg-slate-200 rounded w-full"></div>
                    </div>
                    <div className="flex justify-end">
                      <div className="h-9 w-28 bg-slate-200 rounded-lg"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : normalizedSchedules.length > 0 ? (
              <div className="space-y-5">
                {normalizedSchedules.map((schedule) => {
                  const isPaid = schedule.status.toLowerCase() === "paid" || schedule.status.toLowerCase() === "completed" || schedule.status.toLowerCase() === "fully paid";
                  const isOverdue = schedule.status.toLowerCase().includes("overdue");
                  const totalAmountToPay = schedule.totalDue + schedule.feesDue;
                  const dueStatus = getDueStatus(schedule.dueDate, schedule.status);
                  const accentColor = getCardAccentColor(schedule.status);
                  const cardBg = getCardBackground(schedule.status);

                  return (
                    <div
                      key={schedule.scheduleId}
                      className={`
                        group relative overflow-hidden rounded-2xl border transition-all duration-300 
                        hover:shadow-xl hover:scale-[1.01] shadow-md
                        ${cardBg}
                        ${isOverdue ? "border-rose-200" : "border-slate-200"}
                      `}
                    >
                      {/* Colored top accent bar */}
                      <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${accentColor}`} />

                      <div className="p-6">
                        {/* Header: Installment + Status */}
                        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm">
                              <Hash className="w-4 h-4 text-slate-600" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                                Installment #{schedule.installmentNo}
                              </p>
                              <p className="text-xs font-mono text-slate-400 mt-0.5 flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {schedule.loanProductName} • ID: {schedule.loanId}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`
                              inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium border
                              transition-all duration-200 shadow-sm
                              ${getStatusClasses(schedule.status)}
                            `}
                          >
                            {getStatusIcon(schedule.status)}
                            {schedule.status}
                          </span>
                        </div>

                        {/* Due date & days indicator */}
                        {!isPaid && dueStatus && (
                          <div className="mb-4 flex items-center gap-2 text-sm">
                            <div className={`
                              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                              ${dueStatus.type === "overdue" ? "bg-rose-100 text-rose-800" : ""}
                              ${dueStatus.type === "today" ? "bg-amber-100 text-amber-800" : ""}
                              ${dueStatus.type === "upcoming" ? "bg-blue-100 text-blue-800" : ""}
                              ${dueStatus.type === "future" ? "bg-slate-100 text-slate-700" : ""}
                            `}>
                              <Calendar className="w-3.5 h-3.5" />
                              {dueStatus.label}
                            </div>
                          </div>
                        )}

                        {/* Amounts Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
                          {/* Total amount to pay */}
                          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-slate-100 shadow-sm">
                            <div className="flex items-center gap-2 text-slate-500 mb-1">
                              <DollarSign className="w-4 h-4" />
                              <span className="text-xs font-medium uppercase tracking-wider">
                                Total to Pay
                              </span>
                            </div>
                            <p className="text-2xl font-bold text-slate-900">
                              {formatCurrency(totalAmountToPay)}
                            </p>
                            {!isPaid && schedule.feesDue > 0 && (
                              <p className="text-xs text-rose-600 mt-1 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Includes {formatCurrency(schedule.feesDue)} late fees
                              </p>
                            )}
                          </div>

                          {/* Amount Left to Pay */}
                          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 backdrop-blur-sm rounded-xl p-4 border border-blue-200 shadow-sm">
                            <div className="flex items-center gap-2 text-blue-600 mb-1">
                              <AlertCircle className="w-4 h-4" />
                              <span className="text-xs font-medium uppercase tracking-wider">
                                Amount Left to Pay
                              </span>
                            </div>
                            <p className="text-2xl font-bold text-blue-900">
                              {formatCurrency(schedule.amountLeftToPay)}
                            </p>
                            {schedule.paidTotalSoFar > 0 && (
                              <p className="text-xs text-blue-600 mt-1">
                                Paid so far: {formatCurrency(schedule.paidTotalSoFar)}
                              </p>
                            )}
                          </div>

                          {/* Breakdown */}
                          <div className="space-y-2">
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-slate-500">Principal + Interest</span>
                              <span className="font-medium text-slate-900">
                                {formatCurrency(schedule.totalDue)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-slate-500 flex items-center gap-1">
                                <AlertCircle className="w-3.5 h-3.5 text-rose-500" />
                                Late Fees
                              </span>
                              <span className={`font-medium ${schedule.feesDue > 0 ? "text-rose-600" : "text-slate-500"}`}>
                                {formatCurrency(schedule.feesDue)}
                              </span>
                            </div>
                            {schedule.dueDate && (
                              <div className="flex justify-between items-center text-xs text-slate-400 pt-1 border-t border-slate-100 mt-1">
                                <span>Due date</span>
                                <span className="font-mono">{formatDate(schedule.dueDate)}</span>
                              </div>
                            )}
                          </div>

                          {/* Overdue Information */}
                          {schedule.overdueLeftToPay > 0 && (
                            <div className="bg-gradient-to-br from-rose-50 to-orange-50 backdrop-blur-sm rounded-xl p-4 border border-rose-200 shadow-sm">
                              <div className="flex items-center gap-2 text-rose-600 mb-1">
                                <AlertTriangle className="w-4 h-4" />
                                <span className="text-xs font-medium uppercase tracking-wider">
                                  Overdue Left to Pay
                                </span>
                              </div>
                              <p className="text-2xl font-bold text-rose-900">
                                {formatCurrency(schedule.overdueLeftToPay)}
                              </p>
                              {schedule.overduePaidSoFar > 0 && (
                                <p className="text-xs text-rose-600 mt-1">
                                  Overdue paid: {formatCurrency(schedule.overduePaidSoFar)}
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Action Button */}
                        <div className="flex justify-end border-t border-slate-100 pt-4">
                          {!isPaid ? (
                            <button
                              onClick={() => handleMakePaymentClick(schedule)}
                              className="
                                inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 
                                text-white text-sm font-semibold rounded-xl hover:from-blue-700 hover:to-blue-600 
                                transition-all duration-200 shadow-md shadow-blue-200 hover:shadow-lg 
                                disabled:opacity-50 disabled:cursor-not-allowed
                              "
                              disabled={isMakingPayment}
                            >
                              <DollarSign className="w-4 h-4" />
                              Make Payment
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-50 text-emerald-700 text-sm font-semibold rounded-xl border border-emerald-200">
                              <BadgeCheck className="w-4 h-4" />
                              Paid
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-slate-200 p-12 text-center shadow-sm">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-4">
                  <Calendar className="w-6 h-6 text-slate-500" />
                </div>
                <p className="text-slate-600 font-medium">
                  No repayment schedule found
                </p>
                <p className="text-slate-400 text-sm mt-1 max-w-sm mx-auto">
                  This loan doesn't have a repayment schedule yet. Please contact support.
                </p>
              </div>
            )}

            {/* Payment Instructions */}
            <div className="bg-gradient-to-br from-blue-50/70 to-indigo-50/70 backdrop-blur-sm rounded-2xl border border-blue-100 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-5 flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                How to Make Payments
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div className="flex items-start gap-3 p-3 bg-white/80 rounded-xl">
                  <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                    <Landmark className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Online Transfer</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Secure gateway from your bank
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-white/80 rounded-xl">
                  <div className="p-2 bg-indigo-100 rounded-lg shrink-0">
                    <Smartphone className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Mobile Payment</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Via your banking app
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-white/80 rounded-xl">
                  <div className="p-2 bg-emerald-100 rounded-lg shrink-0">
                    <CreditCard className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">In-Person</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Cash or card at any branch
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-5 pt-4 border-t border-blue-100/50">
                ⚡ Payments after due date may incur late fees. Need help? Contact support.
              </p>
            </div>
          </div>

          {/* RIGHT COLUMN – PAYMENT FORM & RECENT PAYMENTS */}
          <div ref={formRef} className="space-y-6">
            {/* Payment Form Card */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-all duration-200">
              <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
                <div className="p-1.5 bg-blue-50 rounded-lg">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                Make a Payment
              </h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Schedule
                  </label>
                  <div className="relative">
                    <select
                      name="scheduleId"
                      value={repayment.scheduleId}
                      onChange={handleChange}
                      className="
                        w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 text-sm
                        focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                        transition-all appearance-none
                      "
                    >
                      {schedulesLoading ? (
                        <option>Loading schedules...</option>
                      ) : normalizedSchedules.length > 0 ? (
                        normalizedSchedules.map((s) => (
                          <option key={s.scheduleId} value={s.scheduleId}>
                            Installment #{s.installmentNo} — {formatDate(s.dueDate)}
                          </option>
                        ))
                      ) : (
                        <option value="">No schedule available</option>
                      )}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Amount (LKR)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-slate-400">₨</span>
                    </div>
                    <input
                      type="number"
                      name="amount"
                      value={repayment.amount}
                      onChange={handleChange}
                      required
                      placeholder="0.00"
                      className="
                        w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 text-sm
                        focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                        transition-all
                      "
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={repayment.date}
                    onChange={handleChange}
                    required
                    className="
                      w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 text-sm
                      focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                      transition-all
                    "
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Payment Method
                  </label>
                  <div className="relative">
                    <select
                      name="paymentMethod"
                      value={repayment.paymentMethod}
                      onChange={handleChange}
                      className="
                        w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 text-sm
                        focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                        transition-all appearance-none
                      "
                    >
                      <option value="Cash">Cash</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Mobile Money">Mobile Money</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Reference No. (optional)
                  </label>
                  <input
                    type="text"
                    name="referenceNo"
                    value={repayment.referenceNo}
                    onChange={handleChange}
                    placeholder="Auto‑generated"
                    className="
                      w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 text-sm
                      focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                      transition-all
                    "
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Remarks (optional)
                  </label>
                  <input
                    type="text"
                    name="remarks"
                    value={repayment.remarks}
                    onChange={handleChange}
                    placeholder="Auto‑generated"
                    className="
                      w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 text-sm
                      focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                      transition-all
                    "
                  />
                </div>

                {selectedScheduleForPayment && selectedScheduleForPayment.amountLeftToPay <= 0 && (
                  <div className="text-amber-600 text-xs mt-2">
                    ⚠ This schedule is fully paid. No payment required.
                  </div>
                )}

                {incompletePriorSchedule && (
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 mt-2">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
                      <div className="text-rose-700 text-xs">
                        <p className="font-semibold">Payment not available</p>
                        <p className="mt-1">
                          You still have an incomplete transaction for Installment #{incompletePriorSchedule.installmentNo}. 
                          Payment to this schedule is not available until you have fully paid that schedule.
                        </p>
                        <p className="mt-1 font-medium">
                          Remaining amount: {formatCurrency(incompletePriorSchedule.amountLeftToPay)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isConfirmDisabled}
                  className={`
                    w-full py-3.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-xl
                    hover:from-blue-700 hover:to-blue-600 transition-all duration-200 shadow-lg shadow-blue-200
                    ${isConfirmDisabled ? "opacity-50 cursor-not-allowed" : "hover:shadow-xl"}
                  `}
                >
                  {isMakingPayment ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    "Confirm Payment"
                  )}
                </button>
              </form>
            </div>

            {/* Recent Payments
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-all duration-200">
              <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
                <div className="p-1.5 bg-slate-100 rounded-lg">
                  <Clock className="w-5 h-5 text-slate-600" />
                </div>
                Recent Payments
              </h2>
              {paymentsLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-start justify-between animate-pulse">
                      <div className="space-y-2">
                        <div className="h-4 bg-slate-200 rounded w-32"></div>
                        <div className="h-3 bg-slate-200 rounded w-24"></div>
                      </div>
                      <div className="h-4 bg-slate-200 rounded w-16"></div>
                    </div>
                  ))}
                </div>
              ) : payments.length > 0 ? (
                <div className="space-y-5">
                  {payments.slice(0, 5).map((p) => (
                    <div
                      key={p.paymentId || p.id}
                      className="flex items-start justify-between group border-b border-slate-100 pb-4 last:border-0 last:pb-0"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-emerald-50 rounded-lg group-hover:scale-110 transition-transform">
                          <Check className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 text-sm">
                            {p.loanProductName || `Loan #${p.loanId}`}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(p.paymentAt)}
                            </span>
                            <span className="text-xs text-slate-400">•</span>
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              {paymentMethodIcon[p.paymentMethod] || <DollarSign className="w-3 h-3" />}
                              {p.paymentMethod || "Cash"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-900">{formatCurrency(p.paidAmount)}</p>
                        <span className="inline-block text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full mt-1">
                          Completed
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-slate-100 rounded-full mb-3">
                    <Clock className="w-6 h-6 text-slate-500" />
                  </div>
                  <p className="text-slate-600 text-sm">No payments recorded yet.</p>
                  <p className="text-slate-400 text-xs mt-1">Your payment history will appear here.</p>
                </div>
              )}
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Repayment;